import { isAxiosError } from 'axios'

interface ValidationIssue {
  msg?: string
  loc?: (string | number)[]
}

type FastApiDetail = string | ValidationIssue[] | { detail?: string }

/** One refused field in the platform's own validation answer. */
interface PlatformIssue {
  message?: string
}

interface FastApiError {
  detail?: FastApiDetail
  /** The platform's 422: `detail` is only "Validation failed"; the reason for
   *  each field is here, already a sentence that names the field. */
  errors?: PlatformIssue[]
}

const FALLBACK = 'Something went wrong. Please try again.'

function formatPlatformIssues(issues: unknown): string | null {
  if (!Array.isArray(issues)) return null
  const messages = (issues as PlatformIssue[])
    .map((issue) => issue?.message)
    .filter((message): message is string => typeof message === 'string' && message.length > 0)

  return messages.length > 0 ? messages.join('; ') : null
}

function formatValidationIssues(issues: ValidationIssue[]): string | null {
  const messages = issues
    .map(({ loc, msg }) => {
      if (!msg) return null
      const field = loc?.filter((part) => part !== 'body').join('.')
      return field ? `${field}: ${msg}` : msg
    })
    .filter((message): message is string => message !== null)

  return messages.length > 0 ? messages.join('; ') : null
}

function readDetail(detail: FastApiDetail | undefined): string | null {
  if (typeof detail === 'string') return detail || null
  if (Array.isArray(detail)) return formatValidationIssues(detail)
  if (detail && typeof detail === 'object') return detail.detail ?? null
  return null
}

/** What the answer itself says went wrong. The per-field reasons come first:
 *  they say more than the "Validation failed" beside them. */
function readBody(data: FastApiError | undefined): string | null {
  return formatPlatformIssues(data?.errors) ?? readDetail(data?.detail)
}

export function getApiErrorMessage(error: unknown): string {
  if (!isAxiosError<FastApiError>(error)) {
    return error instanceof Error && error.message ? error.message : FALLBACK
  }

  if (error.code === 'ECONNABORTED') return 'The request timed out.'
  if (!error.response) return 'Cannot reach the server. Check your connection.'

  const said = readBody(error.response.data)
  if (said) return said

  return error.response.status >= 500
    ? 'The server ran into a problem. Please try again.'
    : FALLBACK
}

export function isClientError(error: unknown): boolean {
  if (!isAxiosError(error) || !error.response) return false
  return error.response.status >= 400 && error.response.status < 500
}
